import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Info } from "lucide-react";

const TIERS = [
  { id: "gold", price: 0, img: "https://images.pexels.com/photos/7248765/pexels-photo-7248765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["Browse profiles", "Save favorites", "Real Talk visible", "Free forever"] },
  { id: "platinum", price: 15, img: "https://images.unsplash.com/photo-1656055448548-e26753307fc7?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    features: ["Everything in Gold", "Encrypted DMs", "Voice notes for prompts", "Saved search filters", "Boosted visibility", "ID verified badge"] },
  { id: "diamond", price: 30, img: "https://images.unsplash.com/photo-1592136184798-ca0d8e17643a?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    features: ["Everything in Platinum", "Video chat (verified)", "Top placement", "Diamond profile frame", "Concierge support"] },
];

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState("");

  const subscribe = async (tier) => {
    setLoading(tier);
    try {
      const { data } = await api.post("/subscribe", { tier, paypal_order_id: `MOCK-${Date.now()}` });
      toast.success(`Welcome to ${tier.toUpperCase()}! Receipt sent to your email.`);
      await refresh();
      nav("/app");
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed."); }
    finally { setLoading(""); }
  };

  return (
    <div className="min-h-screen p-6 py-12" style={{ background: "var(--lavender)" }}>
      <div className="max-w-6xl mx-auto">
        <Link to="/app" className="text-white/80 hover:text-white text-sm" data-testid="back-to-app">← Back to app</Link>
        <h1 className="font-display text-4xl sm:text-5xl text-white font-extrabold mt-4">Pick your tier</h1>
        <p className="text-white/80 mt-2">Same-size sparkly material previews. Click "What does this include?" for the breakdown.</p>
        <div className="grid sm:grid-cols-3 gap-6 mt-10">
          {TIERS.map(t => (
            <div key={t.id} className="relative rounded-2xl overflow-hidden border-2 border-white/20 fade-up" data-testid={`tier-${t.id}`} style={{ aspectRatio: "3/4" }}>
              <img src={t.img} className="absolute inset-0 w-full h-full object-cover sparkle" alt="" />
              <div className="absolute inset-0 bg-black/60" />
              <div className="relative h-full p-6 flex flex-col">
                <span className="font-display text-3xl font-extrabold text-white capitalize">{t.id}</span>
                <p className="text-white/90 mt-1">{t.price === 0 ? "Free" : `$${t.price}/month`}</p>
                <Dialog>
                  <DialogTrigger asChild>
                    <span data-testid={`tier-info-${t.id}`} className="inline-flex items-center gap-1 text-[#00FFFF] text-sm mt-2 cursor-pointer hover:underline"><Info size={14} /> What does this include?</span>
                  </DialogTrigger>
                  <DialogContent className="glass border-white/15 text-white">
                    <DialogHeader><DialogTitle className="font-display text-2xl capitalize">{t.id} Membership</DialogTitle></DialogHeader>
                    <ul className="space-y-2 mt-2">{t.features.map((f) => <li key={f} className="flex gap-2 text-white/90"><Check size={16} className="text-[#FF1493] mt-1 shrink-0" />{f}</li>)}</ul>
                  </DialogContent>
                </Dialog>
                <div className="mt-auto space-y-2">
                  <Button data-testid={`subscribe-${t.id}`} disabled={loading === t.id || user?.tier === t.id} onClick={() => subscribe(t.id)} className="w-full bg-[#FF1493] hover:bg-[#d1107a] rounded-full font-bold">
                    {user?.tier === t.id ? "Current tier" : loading === t.id ? "Processing..." : t.price === 0 ? "Switch to Gold" : `Subscribe via PayPal`}
                  </Button>
                  {t.price > 0 && <p className="text-white/70 text-xs text-center">Mock PayPal — receipt logged in admin queue.</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
